import React, { useState, useRef, useEffect } from "react";
import {
  createSegment,
  deleteSegment,
  getSegmentAll,
  updateSegment,
  softDeleteSegment,
} from "../../Services/SegmentService";
import { useForm, Controller } from "react-hook-form";
import Swal from "sweetalert2";
import MTable from "../../Components/MTable/MTable";
import { InputSwitch } from "primereact/inputswitch";
import Overlay from "../../Components/Overlay/Overlay";
import { handlerFormError, uuid } from "../../Utils/Utils";
import { toastError, toastSuccess } from "../../Components/Toasts/Toast";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import EditButton from "../../Components/Button/EditButton";
import DeleteButton from "../../Components/Button/DeleteButton";

let processingId = -1;

const SegmentPage = () => {
  let { userInfo } = useSelector((state) => state.auth);
  const [showAll, setShowAll] = useState(false);
  const mTable = useRef();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      segment_code: "",
      segment_name: "",
      init: "",
      is_active: true,
      sort: 0,
    },
  });

  const [state, setState] = useState({
    showForm: false,
    segment: {
      segment_code: "",
      segment_name: "",
      init: "",
      is_active: true,
      sort: 0,
    },
    processing: false,
  });

  const onActiveChange = (item) => (e) => {
    if (!userInfo.access.master_segment.can_update) {
      toastError("You dont have permission for this action");
      return;
    }
    const status = e.target.value ? "Activate" : "Inactive";
    softDeleteSegment(item.id, { is_active: e.target.value })
      .then((res) => {
        toastSuccess(`${status} ${item.segment_name} Success.`);
        mTable.current.refresh();
      })
      .catch(({ response: { err } }) => {
        toastError(`${status} Failed.`);
      });
  };

  const columns = [
    {
      id: uuid(),
      title: "Segment Name",
      field: "segment_name",
      sortable: true,
    },
    {
      id: uuid(),
      title: "Segment Code",
      field: "segment_code",
      sortable: true,
    },
    { id: uuid(), title: "Initial", field: "init", sortable: true },
    {
      id: uuid(),
      title: "Active",
      field: "is_active",
      render: (item) => {
        if (item.is_active === true) {
          return <span className="badge badge-success">Active</span>;
        } else {
          return <span className="badge badge-secondary">Inactive</span>;
        }
      },
    },
    {
      id: uuid(),
      title: "",
      sortable: true,
      style: { width: 200 },
      render: (item) => {
        return (
          <div>
            {userInfo.access.master_segment.can_update && (
              <EditButton title="Edit" onClick={onEdit(item)} />
            )}
            {userInfo.access.master_segment.can_delete && (
              <DeleteButton title="Remove" onClick={onRemove(item)} />
            )}
          </div>
        );
      },
    },
  ];

  const showAllFunc = () => {
    setShowAll(!showAll);
    mTable.current.refresh();
  };

  const propsTable = {
    columns,
    showAllState: showAll,
    showAllFunc,
    getData: (params) => {
      if (showAll === false) {
        if (params.filterAnd) params.filterAnd += "," + `is_active:true`;
        else params.filterAnd = "is_active:true";
      }
      return getSegmentAll({ ...params });
    },
    enableFilter: true,
    showAddButton: !userInfo.access.master_segment.can_create ? false : true,
    showIndex: true,
  };

  const removeData = (id) => {
    deleteSegment(
      id,
      (res) => {
        if (res.status == 200 || res.status == 201) {
          Swal.fire({
            icon: "success",
            title: "Delete data success",
            text: "Data has been deleted!",
          }).then((res) => {
            mTable.current.refresh();
          });
        }
      },
      ({ response: { data } }) => {
        Swal.fire({
          icon: "error",
          title: "Delete data fail",
          text: data.message,
        });
      }
    );
  };

  const onEdit = (item) => () => {
    if (!userInfo.access.master_segment.can_update) {
      return;
    }
    reset(item);
    setState({ ...state, segment: item, showForm: true });
  };

  const onRemove = (item) => () => {
    if (!userInfo.access.master_segment.can_delete) {
      toastError("You dont have permission for this action");
      return;
    }

    Swal.fire({
      icon: "question",
      title: "Are you sure?",
      text: "Deleted data can not be restored!",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
    }).then(({ isConfirmed }) => {
      if (isConfirmed) {
        removeData(item.id);
      }
    });
  };

  const onAddData = () => {
    if (!userInfo.access.master_segment.can_create) {
      return;
    }
    onReset();
    setState({ ...state, showForm: true });
  };

  const startProcessing = () => {
    processingId = setTimeout(() => {
      setState({ ...state, processing: true });
    }, 150);
  };

  const stopProcessing = () => {
    clearTimeout(processingId);
    setState({ ...state, processing: false });
  };

  const onSubmit = (data) => {
    startProcessing();
    const response = segment.id
      ? updateSegment(segment.id, data)
      : createSegment(data);
    response
      .then((res) => {
        if (res.status == 200 || res.status == 201) {
          Swal.fire({
            icon: "success",
            title: "Save data success",
            text: "Data has been saved!",
          }).then((r) => {
            mTable.current.refresh();
            segment.id ? onCloseForm() : onReset();
          });
        }
      })
      .catch(({ response: { data } }) => {
        handlerFormError(data);
      })
      .finally((_) => stopProcessing());
  };

  const onReset = () => {
    reset({
      segment_code: "",
      segment_name: "",
      init: "",
      is_active: true,
      sort: 0,
    });
    setState({ ...state, segment: {} });
  };

  const onCloseForm = () => {
    reset({
      segment_code: "",
      segment_name: "",
      init: "",
      is_active: true,
      sort: 0,
    });
    setState({ ...state, segment: {}, showForm: false });
  };

  const { segment, processing, showForm } = state;

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h4
                style={{
                  marginTop: "0.5rem",
                  marginLeft: "0.2rem",
                  fontWeight: "400",
                }}
              >
                {showForm ? "Segment Form" : "Segment Management"}
              </h4>
              <h6
                style={{
                  marginLeft: "0.2rem",
                  fontWeight: "200",
                  letterSpacing: "1.2px",
                }}
              >
                {showForm
                  ? "Add/Edit a product segment"
                  : "Database of Segments"}
              </h6>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <NavLink to="/home">Home</NavLink>
                </li>
                <li className="breadcrumb-item active">Segment</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <Overlay display={processing} />

                <form name="form-detail" onSubmit={handleSubmit(onSubmit)}>
                  <div className="card-body">
                    <div
                      className={`${
                        !showForm
                          ? "card__side--front-show"
                          : "card__side--front"
                      }`}
                    >
                      <MTable
                        ref={mTable}
                        {...propsTable}
                        onAddData={onAddData}
                        showIndex={true}
                      />
                    </div>

                    <div
                      className={`${
                        showForm ? "card__side--back-show" : "card__side--back"
                      }`}
                    >
                      <div className="row">
                        <div className="col-md-4">
                          <div className="form-group">
                            <label htmlFor="segment_code">Segment Code</label>
                            <input
                              id="segment_code"
                              {...register("segment_code", {
                                required: {
                                  value: true,
                                  message: "Segment code is required!",
                                },
                              })}
                              placeholder="Segment code"
                              className="form-control"
                            />
                            {errors.segment_code && (
                              <span
                                className="text-danger"
                                style={{ fontSize: 14 }}
                              >
                                {errors.segment_code.message}
                              </span>
                            )}
                          </div>
                          <div className="form-group">
                            <label htmlFor="segment_name">Segment Name</label>
                            <input
                              id="segment_name"
                              {...register("segment_name", {
                                required: {
                                  value: true,
                                  message: "Segment name is required!",
                                },
                              })}
                              placeholder="Segment name"
                              className="form-control"
                            />
                            {errors.segment_name && (
                              <span
                                className="text-danger"
                                style={{ fontSize: 14 }}
                              >
                                {errors.segment_name.message}
                              </span>
                            )}
                          </div>
                          <div className="form-group">
                            <label htmlFor="init">Initial</label>
                            <input
                              id="init"
                              {...register("init", {
                                required: {
                                  value: true,
                                  message: "Segment init is required!",
                                },
                              })}
                              placeholder="Segment initials"
                              className="form-control"
                            />
                            {errors.init && (
                              <span
                                className="text-danger"
                                style={{ fontSize: 14 }}
                              >
                                {errors.init.message}
                              </span>
                            )}
                          </div>
                          {segment.id && (
                            <div className="form-group">
                              <label htmlFor="sort">Position Sorting</label>
                              <input
                                id="sort"
                                {...register("sort")}
                                placeholder="Sort"
                                className="form-control"
                              />
                              {errors.sort && (
                                <span
                                  className="text-danger"
                                  style={{ fontSize: 14 }}
                                >
                                  {errors.sort.message}
                                </span>
                              )}
                            </div>
                          )}
                          <div className="form-group">
                            <label htmlFor="is_active">Active</label>
                            <div>
                              <Controller
                                name="is_active"
                                control={control}
                                render={({ field }) => {
                                  return (
                                    <InputSwitch
                                      {...field}
                                      checked={field.value}
                                    />
                                  );
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {showForm && (
                    <div className="card-footer delay">
                      <div className="row">
                        <div className="col-md-12">
                          <div
                            className="d-flex"
                            style={{ justifyContent: "flex-end" }}
                          >
                            <button
                              type="button"
                              className="btn btn-default mr-3"
                              style={{ width: 100 }}
                              onClick={onCloseForm}
                            >
                              <i className="fa fa-times" /> Close
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger mr-3"
                              style={{ width: 100 }}
                              onClick={onReset}
                            >
                              <i className="fa fa-sync" /> Clear
                            </button>
                            <button
                              type="submit"
                              className="btn btn-dark"
                              style={{ width: 100 }}
                              disabled={
                                control._formValues.id
                                  ? !userInfo.access.master_segment.can_update
                                  : !userInfo.access.master_segment.can_create
                              }
                            >
                              <i className="fa fa-save" /> Save
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SegmentPage;
